import { api, type ApiResponse } from './api'
import { descargar, withoutEmpty } from './descarga'

/**
 * Cliente del panel de marketing.
 *
 * El panel no calcula nada: la tabla diaria la arma el servidor, y el Excel sale
 * de esas mismas filas. Recalcular aquí habría significado dos cuentas de
 * «pedidos del día» que podrían no coincidir.
 */

export interface MarketingRange {
  from: string
  to: string
  days: number
}

/**
 * Una fila de la tabla: un día del rango.
 *
 * `day` es el número del día (1, 2, 3…) y `date` la fecha completa: las dos
 * columnas las pide el equipo porque en la hoja de cálculo se trabaja con las
 * dos, y el día de la semana se calcula en el servidor para que no dependa de
 * la zona horaria de quien abre la página.
 */
export interface DailyRow {
  day: number
  date: string
  weekday: string
  /** Pedidos facturados ese día: cotizaciones y ventas de tienda. */
  orders: number
  /** Lo facturado ese día. */
  invoiced: number
  /** Clientes que estrenaron compra ese día. */
  new_customers: number
  /** Clientes que ya habían comprado antes y volvieron ese día. */
  recurring_customers: number
}

/** Una página de la tabla diaria. */
export interface DailyPage {
  total: number
  page: number
  per_page: number
  last_page: number
  data: DailyRow[]
}

export interface MarketingSummary {
  range: MarketingRange
  daily: DailyPage
  /**
   * Totales de **todo el rango**, no de la página.
   *
   * Viajan aparte porque el pie de la tabla describe el periodo que se está
   * mirando: sumar solo los 31 días que caben en pantalla anunciaría menos de
   * lo que se movió, y nadie se daría cuenta hasta cuadrar con el Excel.
   */
  totals: RangeTotals
}

export interface RangeTotals {
  orders: number
  invoiced: number
  new_customers: number
  recurring_customers: number
}

export interface MarketingParams {
  date_from?: string
  date_to?: string
  /** Página de la tabla diaria. */
  page_daily?: number
}

/**
 * La única exportación del panel.
 *
 * El Excel lleva las columnas que el equipo rellena a mano —mensajes,
 * inversión, conversión, ROAS— además de las de la tabla, y por eso una
 * descarga sirve para todos los bloques: no hay un fichero por sección.
 */
export const MARKETING_EXPORTS = [
  { key: 'daily', label: 'Descargar tabla en Excel', path: '/marketing/export/daily' },
] as const

export type MarketingExportKey = (typeof MARKETING_EXPORTS)[number]['key']

export const marketingApi = {
  summary(params: MarketingParams = {}) {
    return api.get<ApiResponse<MarketingSummary>>('/marketing', { params: withoutEmpty(params) })
  },

  async download(path: string, params: MarketingParams = {}): Promise<void> {
    await descargar(path, withoutEmpty(params) as Record<string, unknown>)
  },
}
